import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TextField,
  InputAdornment,
  Pagination,
} from "@mui/material";
import {
  Link as LinkIcon,
  VisibilityOutlined as ViewIcon,
  Search,
} from "@mui/icons-material";
import { useAppContext } from "../../context/AppContext";
import moment from "moment";
import dayjs from "dayjs";
import { minutesToTime } from "../../constants/timeUtils";
import { Grid } from "@mui/system";
import StatsCard from "../../components/StatsCard";
import { getPhysicianUpcomingAppointment } from "../../helpers/AppointmentSchedule";
import Toast from "../../components/toast";
import { openPhysicianMeeting } from "../../helpers/VideoSessions/openPhysicianSession";

const UpcomingPhysicianAppointments = () => {
  const navigate = useNavigate();
  const { isMobile } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [cardsData, setCardsData] = useState([
    { title: "Today's Appointments", value: 0 },
    { title: "This Week's Appointments", value: 0 },
    { title: "Total Appointments", value: 0 },
  ]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 5;

  const GetAllAppointmenthanlder = async () => {
    setIsLoading(true);

    const response = await getPhysicianUpcomingAppointment();
    if (response.statusCode === 200 && response.isSuccess === true) {
      const data = response.data;
      const { todayAppointments, weekAppointments, totalAppointments } =
        getAppointmentStats(data);
      setCardsData([
        { title: "Today's Appointments", value: todayAppointments },
        { title: "This Week's Appointments", value: weekAppointments },
        { title: "Total Appointments", value: totalAppointments },
      ]);
      setAppointments(data ?? []);
    } else {
      Toast.error(response.message);
    }
    setIsLoading(false);
  };

  const filteredAppointments = useMemo(() => {
    const searchTerm = search.toLowerCase();
    return appointments.filter((item) => {
      return (
        item.patientName?.toLowerCase().includes(searchTerm) ||
        moment.utc(item.appointmentDate).format("DD MMM YYYY").toLowerCase().includes(searchTerm) ||
        item.medicalEvaluation?.toLowerCase().includes(searchTerm) ||
        item.appointmentType?.toLowerCase().includes(searchTerm) ||
        item.status?.toLowerCase().includes(searchTerm)
      );
    });
  }, [appointments, search]);

  const paginatedAppointments = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredAppointments.slice(start, start + rowsPerPage);
  }, [filteredAppointments, page]);

  console.log("Filtered Appointments:", paginatedAppointments);
  const pageCount = Math.ceil(filteredAppointments.length / rowsPerPage);

  useEffect(() => {
    GetAllAppointmenthanlder();
  }, []);

  const handleJoinMeeting = (row) => {
    const result = openPhysicianMeeting(row);
    if (result.opened) return;

    Toast.error(
      result.reason === "appointment"
        ? "Appointment is not available"
        : "Zoom link is not available",
    );
  };

  const getAppointmentStats = (appointments) => {
    const now = new Date();

    // Normalize today start & end
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);

    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);

    // Start of week (Monday)
    const startOfWeek = new Date(now);
    const day = startOfWeek.getDay(); // 0 (Sun) - 6 (Sat)

    // Adjust to Monday
    const diffToMonday = day === 0 ? -6 : 1 - day;
    startOfWeek.setDate(startOfWeek.getDate() + diffToMonday);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    let todayCount = 0;
    let weekCount = 0;

    for (const appt of appointments) {
      if (!appt.appointmentDate) continue;

      const apptDate = new Date(appt.appointmentDate);

      if (apptDate >= startOfToday && apptDate <= endOfToday) {
        todayCount++;
      }

      if (apptDate >= startOfWeek && apptDate <= endOfWeek) {
        weekCount++;
      }
    }

    return {
      todayAppointments: todayCount,
      weekAppointments: weekCount,
      totalAppointments: appointments.length,
    };
  };

  const handleViewClick = (row) => {
    if (row.isProviderVisit) {
      navigate("/physician/patient/view", { 
        state: { appointmentId: row._id }, 
      });
    }
    else{
      navigate("/physician/patient-preview", {
        state: { appointmentId: row._id },
      });
    }
  };

  const styles = {
    tableCell: {
      color: "#6B6B6B",
    },
  };

  return (
    <>
      <Box sx={styles.TableContainer}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {cardsData.map((card, index) => (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <StatsCard title={card.title} value={card.value} />
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            border: "1px solid #E8E8E8",
            borderRadius: "12px",
            padding: "10px 10px",
            backgroundColor: "#fff",
          }}
        >
          <Box
            sx={{
              my: 1,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 2,
            }}
          >
            <Typography fontSize={"24px"} className="sf-medium sf-medium--dark">
              All Upcoming Appointments
            </Typography>
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 260,
                background: "#fff",
                borderRadius: "8px",
              }}
            />
          </Box>
          <TableContainer sx={{ overflowY: "auto" }}>
            <Table
              aria-label="simple table"
              sx={{
                border: "1px solid #E8E8E8",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <TableHead sx={{ backgroundColor: "#F5F5FF" }}>
                <TableRow>
                  <TableCell sx={{ ...styles.tableCell }}>
                    Patient Name
                  </TableCell>
                  <TableCell sx={{ ...styles.tableCell }}>
                    Appointment
                  </TableCell>
                  <TableCell sx={{ ...styles.tableCell }}>Video Session</TableCell>
                  <TableCell sx={{ ...styles.tableCell }}>
                    Medical Evaluation
                  </TableCell>
                  <TableCell sx={{ ...styles.tableCell }}>Status</TableCell>
                  <TableCell sx={{ ...styles.tableCell }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAppointments.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.patientName}</TableCell>

                    <TableCell>
                      {moment.utc(row.appointmentDate).format("DD MMM YYYY") +
                        ", " +
                        minutesToTime(row.startMinute) +
                        " - " +
                        minutesToTime(row.endMinute)}
                    </TableCell>

                    <TableCell>
                      <Button
                        sx={{
                          boxShadow: "0px 1px 5px rgb(0 0 0 / 5%)",
                          border: "1px solid #E3E3E5",
                          textTransform: "none",
                          borderRadius: "6px",
                          whiteSpace: "nowrap",
                          color: "#1E1E1EBF",
                        }}
                        onClick={() => handleJoinMeeting(row)}
                      >
                        <LinkIcon sx={{ mr: 0.5, fontSize: 20 }} />
                        Start Meeting
                      </Button>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={
                          {
                            // color: "#7B7DE5",
                            // textDecoration: "underline",
                            // cursor: "pointer",
                          }
                        }
                      >
                        {row.medicalEvaluation ? row.medicalEvaluation : row.appointmentType ? "Provider Visit": "N/A"}
                      </Box>
                    </TableCell>

                    {/* <TableCell>
                      <Box
                        sx={{
                          backgroundColor:
                            row.status === "Completed" ? "#DDF7E6" : "#FFF3DF",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "6px",
                          fontSize: "13px",
                          width: "fit-content",
                          color:
                            row.status === "Completed"
                              ? "#01AC3C"
                              : row.status === "booked" &&
                                  dayjs(row.appointmentDate).isBefore(dayjs())
                                ? "red"
                                : "#FFAA00",
                        }}
                      >
                        {row.status === "booked" &&
                        dayjs(row.appointmentDate).isBefore(dayjs())
                          ? "Missed"
                          : row.status}
                      </Box>
                    </TableCell> */}
                    <TableCell>
                      <Box
                        sx={{
                          backgroundColor:
                            row.formStatus === "Completed" ? "#DDF7E6" : "#FFF3DF",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "6px",
                          fontSize: "13px",
                          width: "fit-content",
                          color:
                            row.formStatus === "Completed"
                              ? "#01AC3C"
                              : row.formStatus === "Pending"
                                ? "red"
                                : "#FFAA00",
                        }}
                      >
                        {row.formStatus}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Button
                        sx={{
                          boxShadow: "0px 1px 5px rgb(0 0 0 / 5%)",
                          border: "1px solid #E3E3E5",
                          textTransform: "none",
                          borderRadius: "6px",
                          color: "#1E1E1EBF",
                        }}
                        onClick={() => handleViewClick(row)}
                      >
                        <ViewIcon sx={{ mr: 0.5, fontSize: 20 }} />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
            }}
          >
            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, value) => setPage(value)}
              shape="circled"
              color="primary"
              variant="outlined"
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
        </Box>
      </Box>
    </>
    // </Container>
  );
};

export default UpcomingPhysicianAppointments;
